import { AppSettings, EditionUpgradeAction } from '@galaxy/marketplace-apps';
import { Frequency, Price, Pricing, RetailPrice } from '@vendasta/marketplace-packages';

export function isCustomUpgrade(appSettings: AppSettings): boolean {
  return appSettings.editionChange?.editionUpgradeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER;
}

export function hasCustomUpgradePrice(appSettings: AppSettings): boolean {
  return !!appSettings.editionChange?.priceReferencePackageId;
}

export function findMostFrequentPrice(pricing: Pricing): RetailPrice {
  if (!pricing || !pricing.prices || pricing.prices.length <= 0) {
    return {
      price: -1,
    } as RetailPrice;
  }
  const normalizeFreeOrContactSalesPrice = (p: Price): Price => {
    if (isPriceFree(p)) {
      p.price = 0;
    } else if (isPriceContactSales(p)) {
      p.price = -1;
    }
    return p;
  };
  const isMoreFrequentThan = (a: Price, b: Price): number => {
    if (isPriceContactSales(a)) {
      return 1;
    }
    const orderedFrequencies = [
      Frequency.DAILY,
      Frequency.WEEKLY,
      Frequency.MONTHLY,
      Frequency.YEARLY,
      Frequency.ONCE,
      Frequency.OTHER,
    ];
    a.frequency = normalizeFrequencyEnum(a.frequency || Frequency.MONTHLY);
    b.frequency = normalizeFrequencyEnum(b.frequency || Frequency.MONTHLY);
    const aIndex = orderedFrequencies.indexOf(a.frequency);
    const bIndex = orderedFrequencies.indexOf(b.frequency);
    return aIndex !== bIndex ? aIndex - bIndex : a.price - b.price;
  };
  const price = pricing.prices
    .map(normalizeFreeOrContactSalesPrice) // convert undefineds and nulls
    .sort(isMoreFrequentThan)[0]; // sort prices by frequency. The first price is the most frequent
  const currency = pricing.currency;
  return {
    price: price.price,
    billingFrequency: price.frequency,
    currency: currency,
  } as RetailPrice;
}

function normalizeFrequencyEnum(value: keyof typeof Frequency | Frequency): Frequency {
  if (typeof value === 'string') {
    return Frequency[value];
  }

  return value;
}

export function isPriceContactSales(price: RetailPrice | Price): boolean {
  return price.price === null || price.price < 0;
}

export function isPriceFree(price: RetailPrice | Price): boolean {
  return price.price === undefined || price.price === 0;
}

const frequencyTranslationsMap: Map<Frequency, string> = new Map<Frequency, string>([
  [Frequency.DAILY, 'DAILY'],
  [Frequency.WEEKLY, 'WEEKLY'],
  [Frequency.MONTHLY, 'MONTHLY'],
  [Frequency.YEARLY, 'YEARLY'],
  [Frequency.ONCE, 'ONCE'],
  [Frequency.OTHER, 'OTHER'],
]);

export function getFrequencyTranslation(prefix: string, frequency: Frequency): string {
  const suffix = frequencyTranslationsMap.get(frequency);
  if (suffix) {
    return `${prefix}.${suffix}`;
  } else {
    return null;
  }
}

export function customUpgradePathSize(appSettings: AppSettings): number {
  if (!isCustomUpgrade(appSettings) || !appSettings.editionChange?.customUpgradePackageIds) {
    return 0;
  }

  return appSettings.editionChange.customUpgradePackageIds.length;
}

export function isProUpgrade(appSettings: AppSettings): boolean {
  if (!appSettings.editionChange) {
    return true;
  }
  return (
    appSettings.editionChange.editionUpgradeAction !== EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER &&
    appSettings.editionChange.editionUpgradeAction !== EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES
  );
}
